import { Link } from "react-router-dom";
import {
  Heart,
  Users,
  Sparkles,
  MapPin,
  ArrowRight,
  Quote,
  Compass,
  Feather,
  Award,
  Leaf,
  Home as HomeIcon,
} from "lucide-react";
import MarketingLayout from "../../components/MarketingLayout";
import { Brand, brandShort } from "../../context/CompanyContext";

const IMG = {
  // Madeira coast — abertura do capítulo
  coast: "https://images.pexels.com/photos/2356045/pexels-photo-2356045.jpeg?auto=compress&w=1600",
  // Caderno com a primeira ideia anotada
  notebook: "https://images.pexels.com/photos/4144222/pexels-photo-4144222.jpeg?auto=compress&w=1400",
  // Fundador num quarto preparado / interior cuidado
  founder: "https://images.pexels.com/photos/6585605/pexels-photo-6585605.jpeg?auto=compress&w=1400",
  // Quarto pronto para check-in (estilo hoteleiro)
  bedroom: "https://images.pexels.com/photos/6585756/pexels-photo-6585756.jpeg?auto=compress&w=1600",
  // Mãos da família — assinatura de pertença
  familyHands: "https://images.pexels.com/photos/7691716/pexels-photo-7691716.jpeg?auto=compress&w=1400",
  // Equipa fardada / sorrisos / proximidade
  team: "https://images.pexels.com/photos/4099354/pexels-photo-4099354.jpeg?auto=compress&w=1600",
  // Funchal / vista da ilha
  funchal: "https://images.pexels.com/photos/15286/pexels-photo.jpg?auto=compress&w=1600",
  // Toalhas dobradas / detalhe
  detail: "https://images.pexels.com/photos/3935331/pexels-photo-3935331.jpeg?auto=compress&w=1400",
};

const MILESTONES = [
  {
    year: "Londres · 2023",
    title: "Anos a trabalhar no terreno",
    text:
      "Victor Souto e a sua esposa, Thayse Souto, trabalham em Londres na área das limpezas e da gestão de alojamentos. É lá — entre check-outs apertados e padrões hoteleiros exigentes — que aprendem o ofício que mais tarde trariam para a Madeira.",
    icon: Compass,
  },
  {
    year: "Londres · 2024",
    title: "A ideia no papel",
    text:
      "Já com anos de experiência juntos, o casal começa a esboçar — num bloco de notas — o modelo de uma empresa própria. Tudo o que tinham aprendido em Londres, aplicado à realidade dos anfitriões da Madeira.",
    icon: Feather,
  },
  {
    year: "Madeira · 2025",
    title: "Os primeiros apartamentos",
    text:
      "Com a ideia amadurecida, dão o salto e arrancam na ilha. Em poucas semanas, três anfitriões aceitam testar o conceito — sem contratos, só palavra dada.",
    icon: HomeIcon,
  },
  {
    year: "Madeira · 2025",
    title: "A primeira equipa fardada",
    text:
      `A ${brandShort()} deixa de ser um nome em papel: nasce o uniforme, o checklist e o princípio que nunca abandonámos — a mesma equipa, sempre no mesmo apartamento.`,
    icon: Users,
  },
  {
    year: "Madeira · final 2025",
    title: "A plataforma que faltava",
    text:
      `Quase um ano e meio depois das primeiras anotações em Londres, construímos o sistema digital ${brandShort()}: agendas, fotografias, ocorrências e faturação — tudo no mesmo sítio, à medida da nossa realidade.`,
    icon: Sparkles,
  },
  {
    year: "2026",
    title: "Madeira inteira ao nosso cuidado",
    text:
      "Servimos anfitriões em Funchal, Caniço, Ponta do Sol e Câmara de Lobos. Continuamos uma empresa familiar — só que agora com muitos amigos novos.",
    icon: Compass,
  },
];

function Eyebrow({ children }) {
  return (
    <span className="inline-block text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
      {children}
    </span>
  );
}

function Chapter({ kicker, title, children, image, side = "right" }) {
  return (
    <section className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center my-24 sm:my-32">
      <div className={side === "left" ? "lg:order-2" : ""}>
        <Eyebrow>{kicker}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#fdfbf7] leading-tight">
          {title}
        </h2>
        <div className="mt-6 space-y-5 text-[15px] leading-[1.85] text-[#a0b2af]">
          {children}
        </div>
      </div>
      <figure className={`relative ${side === "left" ? "lg:order-1" : ""}`}>
        <div className="absolute -inset-3 border border-[#d4af37]/30 rounded-sm pointer-events-none"></div>
        <img
          src={image}
          alt=""
          loading="lazy"
          className="relative w-full h-[420px] sm:h-[480px] object-cover rounded-sm shadow-xl shadow-black/40"
        />
      </figure>
    </section>
  );
}

export default function Historia() {
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMG.coast}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#031411] via-[#031411]/85 to-[#031411]"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-36 text-center">
          <Eyebrow>A nossa história</Eyebrow>
          <h1 className="mt-5 font-display text-4xl sm:text-6xl tracking-tight text-[#fdfbf7] leading-[1.1]">
            De <span className="italic text-[#d4af37]">Londres</span> para a{" "}
            <br className="hidden sm:block" />
            ilha da Madeira —{" "}
            <br className="hidden sm:block" />
            uma história a dois.
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-[#a0b2af] leading-relaxed">
            A <Brand /> começou longe daqui. Nasceu da experiência de
            um casal que, durante anos, trabalhou no setor das limpezas e
            do alojamento local em Londres — e que, ano e meio depois de
            rabiscar a primeira ideia num bloco de notas, decidiu trazer
            tudo o que sabia para a ilha.
          </p>
        </div>
      </section>

      {/* INTRO QUOTE */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <Quote size={36} className="mx-auto text-[#d4af37]" />
        <blockquote className="mt-6 font-display text-2xl sm:text-3xl text-[#fdfbf7] leading-snug italic">
          “Aprendemos o ofício em Londres. Construímo-lo, do zero, aqui na Madeira.”
        </blockquote>
        <div className="mt-6 text-xs uppercase tracking-[0.3em] text-[#a0b2af]">
          Victor Souto & Thayse Souto · fundadores
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* CAPÍTULO I */}
        <Chapter
          kicker="Capítulo I · Londres"
          title="Onde tudo se aprendeu."
          image={IMG.notebook}
        >
          <p>
            Antes de existir um nome, existia um ritmo: o ritmo dos
            check-outs apertados em Londres, dos quartos a entregar em
            tempo recorde, dos hóspedes internacionais a chegar à hora
            mais inconveniente. Foi nesse ritmo que Victor Souto e a
            sua esposa, Thayse Souto, viveram durante anos.
          </p>
          <p>
            Ambos trabalhavam no setor das limpezas e da gestão de
            alojamento local na capital britânica. Foi lá — entre turnos
            longos e padrões hoteleiros exigentes — que aprenderam o
            ofício e perceberam, em primeira mão, o que separa um serviço
            que se nota daquele que passa despercebido.
          </p>
          <p>
            Foi também lá que começaram a anotar, com método, tudo o que
            achavam que se podia fazer melhor:{" "}
            <strong className="text-[#fdfbf7]">consistência</strong>,{" "}
            <strong className="text-[#fdfbf7]">protocolos claros</strong>,{" "}
            <strong className="text-[#fdfbf7]">a mesma equipa de
            sempre</strong> no mesmo apartamento. Aquilo que faltava,
            quando regressavam em pensamento à Madeira.
          </p>
        </Chapter>

        {/* CAPÍTULO II */}
        <Chapter
          kicker="Capítulo II · O casal fundador"
          title="Uma empresa fundada a dois."
          image={IMG.founder}
          side="left"
        >
          <p>
            A <Brand /> é, antes de tudo, uma{" "}
            <strong className="text-[#fdfbf7]">empresa familiar</strong>.
            Foi fundada por <strong className="text-[#fdfbf7]">Victor Souto</strong>{" "}
            e pela sua esposa,{" "}
            <strong className="text-[#fdfbf7]">Thayse Souto</strong> —
            um casal que já partilhava, antes da empresa, a mesma
            profissão.
          </p>
          <p>
            Foram colegas em Londres muito antes de serem sócios na
            ilha. Conheciam-se o suficiente para saber que aguentavam
            os dias longos e respeitavam-se o suficiente para confiar
            um no outro quando algo corria menos bem. Esse é, talvez,
            o verdadeiro motor desta empresa.
          </p>
          <p>
            Hoje continuam a contratar pelo carácter primeiro, pela
            técnica depois. Porque entrar em casa de alguém é um ato
            de confiança — e essa, aprenderam a dois, não se compra.
          </p>
        </Chapter>

        {/* CAPÍTULO III */}
        <Chapter
          kicker="Capítulo III · A travessia"
          title="Quase ano e meio entre o papel e a realidade."
          image={IMG.bedroom}
        >
          <p>
            Da primeira ideia rabiscada num bloco de notas em Londres,
            até à primeira chave entregue na Madeira, passou{" "}
            <strong className="text-[#fdfbf7]">quase ano e meio</strong>.
            Tempo a desenhar o serviço, a estudar a ilha, a poupar, a
            decidir.
          </p>
          <p>
            Quando finalmente arrancaram, ligaram a três anfitriões que
            conheciam há anos e propuseram algo invulgar: deixem-nos
            cuidar dos vossos apartamentos durante 90 dias.{" "}
            <strong className="text-[#fdfbf7]">Sem contrato. Sem
            fidelização. Sem promessas exageradas.</strong> Só palavra
            dada.
          </p>
          <p>
            Os três aceitaram. Foi com eles que afinámos o que hoje é o
            nosso protocolo: tempo médio por tipologia, checklists
            fotográficos, encomendas de produtos, lavandaria com prazos
            apertados, comunicação imediata em ocorrências. Tudo o que
            tínhamos escrito em Londres, posto à prova no terreno
            madeirense.
          </p>
          <p>
            Ao fim dos 90 dias, os três continuaram. E recomendaram-nos.
            Foi assim — boca-a-boca, sem marketing, sem anúncios — que a
            <Brand short /> deixou de ser uma ideia para ser, finalmente,{" "}
            <em className="text-[#d4af37]">uma empresa</em>.
          </p>
        </Chapter>

        {/* CAPÍTULO IV — Citação com fundo */}
        <section className="my-24 sm:my-32 relative overflow-hidden rounded-sm">
          <img
            src={IMG.familyHands}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#031411] via-[#031411]/95 to-[#031411]/70"></div>
          <div className="relative px-6 sm:px-12 py-20 sm:py-28 max-w-3xl">
            <Eyebrow>Capítulo IV · O nosso princípio</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-5xl text-[#fdfbf7] leading-[1.1]">
              A mesma equipa,
              <br />
              <span className="italic text-[#d4af37]">sempre</span> no
              mesmo apartamento.
            </h2>
            <p className="mt-6 text-[15px] leading-[1.85] text-[#a0b2af]">
              É a regra mais simples da casa — e também a mais difícil de
              cumprir. Mas é ela que faz com que a Maria conheça a manha
              da porta do T2 de Câmara de Lobos. Que o João saiba onde
              fica a chave extra do T1 do Funchal. Que ninguém perca tempo
              a reaprender o que outra pessoa já sabia.
            </p>
            <p className="mt-3 text-[15px] leading-[1.85] text-[#a0b2af]">
              Para o anfitrião, isto significa <strong className="text-[#fdfbf7]">consistência
              hoteleira</strong> em casas que, no fundo, continuam a ser
              suas. Para nós, significa equipas que se orgulham daquilo
              que fazem.
            </p>
          </div>
        </section>

        {/* CAPÍTULO V */}
        <Chapter
          kicker="Capítulo V · A equipa"
          title="Profissionais. Fardados. Daqui."
          image={IMG.team}
          side="left"
        >
          <p>
            Hoje a <Brand /> é composta por uma equipa de
            profissionais residentes na ilha — todos com formação interna,
            todos com farda <Brand short />, todos com sinalética identificadora
            visível.
          </p>
          <p>
            Cada elemento da equipa tem o seu próprio painel digital, onde
            recebe a agenda do dia, regista fotografias antes/depois,
            comunica ocorrências e mantém o stock de produtos sempre
            visível para o anfitrião. Não é um luxo — é a forma como
            evitamos as surpresas que dão má reputação ao sector.
          </p>
          <p>
            Investimos na carreira de cada pessoa que entra para a casa.
            Porque cuidar bem dos colaboradores é a única forma honesta
            de eles cuidarem bem das casas dos nossos clientes.
          </p>
        </Chapter>

        {/* MARCOS */}
        <section className="my-24 sm:my-32">
          <div className="text-center max-w-3xl mx-auto">
            <Eyebrow>Marcos</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#fdfbf7] leading-tight">
              Uma linha do tempo
              <br />
              <span className="italic text-[#d4af37]">honesta.</span>
            </h2>
            <p className="mt-4 text-[#a0b2af] text-[15px] leading-relaxed">
              Não inventamos números. Estes são, literalmente, os
              momentos que nos trouxeram até aqui.
            </p>
          </div>

          <ol className="mt-14 relative border-l border-[#1a4640] max-w-3xl mx-auto pl-8">
            {MILESTONES.map((m, i) => {
              const Icon = m.icon;
              return (
                <li key={i} className="mb-12 last:mb-0 relative">
                  <span className="absolute -left-[44px] top-0 w-9 h-9 rounded-full bg-[#031411] border border-[#d4af37] grid place-items-center text-[#d4af37]">
                    <Icon size={15} />
                  </span>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">
                    {m.year}
                  </div>
                  <h3 className="mt-1 font-display text-xl text-[#fdfbf7]">
                    {m.title}
                  </h3>
                  <p className="mt-2 text-[#a0b2af] text-[15px] leading-[1.8]">
                    {m.text}
                  </p>
                </li>
              );
            })}
          </ol>
        </section>

        {/* CAPÍTULO VI */}
        <Chapter
          kicker="Capítulo VI · Hoje"
          title="Servir uma ilha inteira, mantendo-nos pequenos."
          image={IMG.funchal}
        >
          <p>
            Estamos presentes no <strong className="text-[#fdfbf7]">Funchal</strong>,
            <strong className="text-[#fdfbf7]"> Caniço</strong>,
            <strong className="text-[#fdfbf7]"> Ponta do Sol</strong> e
            <strong className="text-[#fdfbf7]"> Câmara de Lobos</strong> — e
            continuamos a crescer ao ritmo das casas que conseguimos
            cuidar pessoalmente.
          </p>
          <p>
            Recusámos, no último ano, mais propostas do que aceitámos.
            Não por falta de ambição, mas pelo contrário: porque
            preferimos crescer devagar a aceitar trabalho que não
            conseguíamos manter ao mesmo nível.
          </p>
          <p>
            É essa a promessa que fazemos a cada novo anfitrião —{" "}
            <em className="text-[#d4af37]">nunca seremos demasiado grandes
            para responder ao seu WhatsApp em cinco minutos</em>.
          </p>
        </Chapter>

        {/* VALORES */}
        <section className="my-24 sm:my-32">
          <div className="text-center">
            <Eyebrow>O que nos define</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#fdfbf7]">
              Quatro princípios que <span className="italic text-[#d4af37]">não negociamos</span>.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Heart, t: "Família primeiro", d: "Tratamos cada cliente como se fosse de casa. E cada colaborador como nosso." },
              { icon: Award, t: "Padrão hoteleiro", d: "Protocolos rigorosos, mas com a alma de quem cuida — não de uma operação industrial." },
              { icon: Leaf, t: "Madeira, sempre", d: "100% madeirense. Produtos locais sempre que possível, fornecedores da terra." },
              { icon: Sparkles, t: "Discrição", d: "Entramos, fazemos o nosso trabalho, deixamos o melhor de nós. Sem ruído." },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <article
                  key={i}
                  className="p-6 rounded-sm bg-[#062c27] border border-[#d4af37]/20 hover:border-[#d4af37]/60 transition-colors"
                  data-testid={`historia-valor-${i}`}
                >
                  <div className="w-11 h-11 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 grid place-items-center text-[#d4af37]">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-display text-lg text-[#fdfbf7]">
                    {v.t}
                  </h3>
                  <p className="mt-2 text-sm text-[#a0b2af] leading-relaxed">
                    {v.d}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* DETAIL + CARTA */}
        <section className="my-24 sm:my-32 grid lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          <figure className="lg:col-span-2 relative">
            <div className="absolute -inset-3 border border-[#d4af37]/30 pointer-events-none rounded-sm"></div>
            <img
              src={IMG.detail}
              alt=""
              loading="lazy"
              className="relative w-full h-[440px] object-cover rounded-sm shadow-xl shadow-black/40"
            />
          </figure>
          <div className="lg:col-span-3">
            <Eyebrow>Uma carta breve dos fundadores</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#fdfbf7] leading-tight">
              A todos os que confiam a sua casa nas nossas mãos.
            </h2>
            <div className="mt-6 space-y-5 text-[15px] leading-[1.85] text-[#a0b2af]">
              <p>
                A <Brand /> começou longe daqui. Eu, Victor Souto,
                e a minha esposa, Thayse Souto, passámos anos a
                trabalhar nesta área em Londres — a aprender, sem
                pressas, aquilo que distingue um serviço que se nota
                daquele que passa despercebido. Foi de lá que trouxemos
                as primeiras notas para um caderno.
              </p>
              <p>
                Quase ano e meio depois dessas primeiras anotações, a
                <Brand short /> tornou-se realidade na ilha. Não veio fácil:
                houve adiamentos, dúvidas, ajustes. Mas chegámos — com
                a certeza tranquila de que sabíamos o que estávamos a
                propor, porque já o tínhamos feito todos os dias do
                outro lado do mar.
              </p>
              <p>
                Se hoje somos uma equipa maior, é porque vocês —
                anfitriões — confiaram em nós quando ainda éramos só
                duas pessoas a recomeçar. Esta empresa cresceu pelas
                vossas mãos. E é pelas vossas mãos que continuaremos a
                crescer, sempre com cuidado.
              </p>
              <p className="text-[#fdfbf7]">
                — Victor Souto &amp; Thayse Souto, fundadores da <Brand />.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="my-20 sm:my-28 text-center max-w-3xl mx-auto">
          <Eyebrow>Quer fazer parte da próxima página?</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl text-[#fdfbf7] leading-tight">
            Conte-nos sobre o seu apartamento.
            <br />
            Tratamos do resto.
          </h2>
          <p className="mt-6 text-[15px] leading-[1.85] text-[#a0b2af]">
            Se chegou até aqui é porque, provavelmente, partilha connosco
            uma certa exigência. Marquemos uma conversa. Sem compromisso.
          </p>
          <Link
            to="/#contacto"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-sm text-[#031411] bg-[#d4af37] hover:bg-[#f5d76e] font-semibold uppercase tracking-widest text-sm shadow-lg shadow-[#d4af37]/20 transition-colors"
            data-testid="historia-cta"
          >
            Falar connosco <ArrowRight size={18} />
          </Link>
          <div className="mt-6 flex items-center justify-center gap-3 text-[11px] text-[#a0b2af]">
            <MapPin size={12} className="text-[#d4af37]" />
            Atendimento exclusivamente para anfitriões da ilha da Madeira.
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
